import type {ListItemBase} from "@t/list/List";

export type ColType = string;

export interface ColTypeMap {
    [key:string]: ColType;
}
export const ColumnType:ColTypeMap = {
	SELECT: "select",		// column with row selection checkbox
	DELETE: "delete",		// column with row delete icon
	SORT: "sort",			// column with a sorting handle

    STRING: 'S',
    HTML: 'H',
    TEXT: 'Q',
    DATE: 'D',
    DATETIME: '*',
    DATE_AND_TIME: '!',
    TIME: '?',
    INT: 'I',
    LONG: 'V',
    FLOAT: 'F',
    BOOLEAN: 'B',
    SET: 'T',
    TRINITY: '3',
    USER: 'U',
    WORD: 'W', //alows {=,<>,in, not in}

	// References to other row objects
	PARTY: 'C',
    OPPORTUNITY: 'O',
    OFFER: 'E',
    BUSCASE: 'K',
    BCITEM: 'X', // deprecated
    TASK: 'Y',
    EVENT: 'Z',
    PROBLEM: 'G',
    REGION: 'N',
	CAMPAIGN: 'J',
	PRODUCT: 'A', //Article

    MULTIPLE: 'M',
    EADDRESS: '@',
    FULLTEXT: '_', //general key - int which can be null instead 0
    KEY: '^', //general key - int which can be null instead 0
    KEY_MULTI: ':', //general multiple key - value,value
    LIST: 'L'		// BusCaseTypeItem
};

export function defaultBudget(type: ColType):number {
	switch (type) {
		case ColumnType.STRING: return 20;
		case ColumnType.HTML: return 30;
		case ColumnType.TEXT: return 30;
		case ColumnType.DATE: return 10;
		case ColumnType.DATETIME: return 16;
		case ColumnType.DATE_AND_TIME: return 16;
		case ColumnType.TIME: return 5;
		case ColumnType.INT: return 8;
		case ColumnType.LONG: return 12;
		case ColumnType.FLOAT: return 12;
		case ColumnType.BOOLEAN: return 2;
		case ColumnType.SET: return 30;
		case ColumnType.TRINITY: return 30;
		case ColumnType.USER: return 12;
		case ColumnType.WORD: return 30;
		case ColumnType.PARTY:
		case ColumnType.OPPORTUNITY:
		case ColumnType.OFFER:
		case ColumnType.BUSCASE:
		case ColumnType.BCITEM:
		case ColumnType.TASK:
		case ColumnType.EVENT:
		case ColumnType.PROBLEM:
		case ColumnType.REGION:
		case ColumnType.CAMPAIGN:
		case ColumnType.PRODUCT:
			return 20;
		default:
			return 10;
	}
}

export type LayoutMode =
	"normal"    // standard layout mode (normal layouting based on budget)
	| "left"	// stick to the left (spans all runs)
	| "right"	// stick to the right (spans all runs)
	| "top"		// column is spanned over all data columns (excl. sticking columns) and kept at the top
	| "bottom"	// column is spanned over all data columns (excl. sticking columns) and kept at the bottom
	;

export interface ColumnLayout {
	name: string;
	type?: ColType;
	layout?: LayoutMode;
	/** The column will be rendered with fixed with. The value must be specified in CSS units. It should be specified without horizontal padding of table cells (net width).  */
	fixedWidth?: string;
}

/**
 * Sometimes it is useful to attach formatting information to a column
 * in the view. This object allows to associate a column name with
 * styling and formatting info.
 */
export interface ColumnStyle {
	justify?: "left" | "right" | "center";
	size?: "small" | "normal" | "large";
	weight?: "thin" | "normal" | "thick" | "bold";
	/** If set to true the column will span over all other columns on separate row */
	rowType?: boolean;
	/** ID of EventColor to tint foreground color */
	fg?: number;
	/** ID of EventColor to tint background color */
	bg?: number;
}

export interface Column extends ColumnLayout, ColumnStyle {
    name: string;
    id: string;
    /** Full name is available only if it is different from the name */
    fullName?: string;
    title: string;
    /** Java script object property (if missing, name is used ) */
    property?: string;
    type: ColType;
    listName?: string;
    canSort?: boolean;
    canFilter?: boolean;
    summary?: boolean;
    showInAgenda?: boolean;
    fakeColumn?: boolean;
    editable?: boolean;
    qbeEnable?: boolean;
    folderColumn?: boolean;
    useInSelect?: boolean;
    sensitive?: boolean;
    useAlias?: boolean;
    alias?: string;
    description?: string;
    requiredRole?: string;
    listListener?: string;
}

/**
 * See ColumnOption.toJson()
 */
export interface ColumnListItem extends ListItemBase, ColumnStyle {
    type: ColType;
	list?: string;
	primaryKey?: boolean;
	/** This column cannot be used for filtering (filter editting) */
	noFilter?: boolean;
	/** Cannot be shown in agenda */
	noView?: boolean;
	/** Cannot be sorted by */
	noSort?: boolean;
}

